import React from 'react';
import round from 'utils/round';
import RepProfilePageActions from 'actions/RepProfilePageActions';
import LoadingOverlay from 'components/pure/utils/LoadingOverlay';
import RepActivityItem from './RepActivityItem';
import Theme from 'styles/theme';
import RepProfileSectionHeader from './RepProfileSectionHeader';
import HelpText from './tooltips/HelpText';
import './RepProfileActivities.styl';

const disclaimer =
  'Listening to any portion of a call counts towards this total; however, multiple listens to a call on the same day do not add to the total.';

export default class RepProfileCoachingActivities extends React.PureComponent {
  render() {
    const {
      coachingActivity,
      coachingTeamMetrics,
      rep,
      isCoachingActivityLoading,
      isCoachingTeamMetricsLoading,
      periodText,
      startDate,
      endDate,
      headerStartDate,
      headerEndDate,
      renderTeamAverage,
    } = this.props;
    const isReviewing = this.props.reviewType === 'review';
    const callsAdjective = isReviewing ? 'their own' : "other reps'";

    const dataGetterForType = type => () =>
      RepProfilePageActions.getTrendData({
        userId: rep.id,
        direction: this.props.reviewType,
        type,
        startDate,
        endDate,
      });

    const color = isReviewing ? Theme.palette.outrageousOrange : Theme.palette.smoky;

    return coachingActivity || isCoachingActivityLoading ? (
      <section className="rep-profile-activities main-section">
        {(isCoachingActivityLoading || isCoachingTeamMetricsLoading) && <LoadingOverlay />}
        {coachingActivity && (
          <section className="stats">
            <RepProfileSectionHeader
              showTeamIcon={renderTeamAverage}
              title={
                (rep ? `${rep.firstName}'s ` : '') +
                (isReviewing ? 'Review Activities' : 'Coaching Activities')
              }
              startDate={headerStartDate}
              endDate={headerEndDate}
              location={this.props.location}
            />
            <div className="secondary-section-header">Activities</div>
            <div className="sub-section">
              <RepActivityItem
                startDate={headerStartDate}
                endDate={headerEndDate}
                rep={rep}
                repValue={coachingActivity.callsListenedTo}
                teamAverage={renderTeamAverage && coachingTeamMetrics.callsListenedToAvg}
                max={coachingTeamMetrics.callsListenedToMax}
                title="Calls Listened To"
                color={color}
                fetchTrendData={dataGetterForType('listening_count')}
                helpText={() => (
                  <HelpText
                    text={`The total number of ${callsAdjective} calls that the rep listened to during the ${periodText} period of the report.`}
                    disclaimer={disclaimer}
                  />
                )}
              />
              <RepActivityItem
                startDate={headerStartDate}
                endDate={headerEndDate}
                percentageBased
                rep={rep}
                repValue={coachingActivity.avgEachCallListenedTo}
                teamAverage={renderTeamAverage && coachingTeamMetrics.avgEachCallListenedToAvg}
                max={coachingTeamMetrics.avgEachCallListenedToMax}
                title="Average of Each Call Listened To"
                formatValue={value => round(value * 100) + '%'}
                color={color}
                fetchTrendData={dataGetterForType('listening_pct')}
                helpText={() => (
                  <HelpText
                    text={`The average percentage of ${callsAdjective} calls that the rep listened to during the ${periodText} period of the report.`}
                    disclaimer={disclaimer}
                  />
                )}
              />
              <RepActivityItem
                startDate={headerStartDate}
                endDate={headerEndDate}
                rep={rep}
                repValue={coachingActivity.commentsCreated}
                teamAverage={renderTeamAverage && coachingTeamMetrics.commentsCreatedAvg}
                max={coachingTeamMetrics.commentsCreatedMax}
                title="Comments Created"
                color={color}
                fetchTrendData={dataGetterForType('comments_count')}
                helpText={() =>
                  `The total number of comments that the rep left on ${callsAdjective} calls during the ${periodText} period of the report.`
                }
              />
              <RepActivityItem
                startDate={headerStartDate}
                endDate={headerEndDate}
                rep={rep}
                repValue={coachingActivity.callsAddedToLibraries}
                teamAverage={renderTeamAverage && coachingTeamMetrics.callsAddedToLibrariesAvg}
                max={coachingTeamMetrics.callsAddedToLibrariesMax}
                title="Calls Added To Shared Libraries"
                color={color}
                fetchTrendData={dataGetterForType('library_adds')}
                helpText={() =>
                  `The total number of ${callsAdjective} calls that the rep added to shared libraries during the ${periodText} period of the report. Calls added to individual libraries do not count towards this total.`
                }
              />
              <RepActivityItem
                startDate={headerStartDate}
                endDate={headerEndDate}
                rep={rep}
                repValue={coachingActivity.scoreCardsCompleted}
                teamAverage={renderTeamAverage && coachingTeamMetrics.scoreCardsCompletedAvg}
                max={coachingTeamMetrics.scoreCardsCompletedMax}
                title="Scorecards Completed"
                color={color}
                fetchTrendData={dataGetterForType('calls_scored')}
                helpText={() =>
                  `The total number of scorecards that the rep completed on ${callsAdjective} calls during the ${periodText} period of the report.`
                }
              />
            </div>
          </section>
        )}
      </section>
    ) : null;
  }
}
